/**
 * LANDING ON A PLACE IN A PAGE (2026-10-19: "when a user clicks on a button to go to a portion of a public page … it
 * should be centered nicely and land in an intuitive obvious way on that section, not just tucked at the top").
 *
 * A link to a place on a page (`/#control`, `/pricing#faq`), pressed on that page or arrived at from another, brings
 * the place to the middle of what the person can see: the band between the sticky header's foot and the top of the
 * closed chat bar (src/utils/chatBarReach.ts). A place taller than that band lands its top LANDING_AIR_PX under the
 * header instead, so its heading is never cut off. A whole section is landed by its words (the boxes of its children),
 * never by its padding, and every box is read from the layout, never from the screen: a section still waiting to slide
 * in (the home page's arrival, a transform) is measured where it will stand.
 *
 * A page may name the highest place a landing may rest, with LANDING_FLOOR_ATTRIBUTE on an element: no landing stops
 * the page above where that element lands by its own scroll margin. The home page names its first section's words,
 * where the hero's dive ends, so a landing never leaves the page inside the dive.
 *
 * THE BAND CAN ARRIVE LATE. The closed chat bar loads in its own chunk, and on a first visit it can appear after the
 * page has landed (measured on prest0.ai/#integrations, 2026-10-19: centred in the whole window, then 65px low once the
 * bar covered the foot). So for RELAND_WINDOW_MS after a landing, a change in how far the bar reaches lands the same
 * place again, unless the person has taken the page themselves (a wheel, a touch, a key, a press) since.
 *
 * Documents (the reading pages, an insights post) keep landing a heading at the top, as a reader expects: that is
 * kit/arrival/useArriveAtAnchor.ts's own choice, not this module's.
 *
 * No React here: the first download (today's footer), the shell (the header's menus, the footer), the kit and the home
 * page all read it.
 */
import { getChatBarReach, subscribeChatBarReach } from './chatBarReach';

/** The air kept between the header's foot and a place too tall to centre. */
export const LANDING_AIR_PX = 16;

/** How long after a landing a late change to the band (the chat bar arriving) lands the place again. */
export const RELAND_WINDOW_MS = 5000;

/** Marks the highest place a landing may rest on this page (the home page's first section, where the dive ends). */
export const LANDING_FLOOR_ATTRIBUTE = 'data-landing-floor';

export interface LandingGeometry {
  /** The place's top and bottom, in px from the top of the page. */
  top: number;
  bottom: number;
  /** The window's height (px). */
  viewport: number;
  /** How far down the window the sticky header covers it (px). */
  header: number;
  /** How far up the window the closed chat bar covers it (px). */
  footer: number;
  /** How far the page can scroll (px). */
  maxScroll: number;
  /** The highest scroll position a landing may rest at (px), when the page names one. */
  floor?: number;
}

/**
 * Where the page must scroll to land a place: centred in the band it can be seen in when it fits there with air above
 * and below, else its top LANDING_AIR_PX under the header; never above the page's floor, never past its end. PURE.
 */
export function landingScrollTop(g: LandingGeometry): number {
  const band = Math.max(0, g.viewport - g.header - g.footer);
  const height = Math.max(0, g.bottom - g.top);
  const y = height + 2 * LANDING_AIR_PX <= band ? g.top - g.header - (band - height) / 2 : g.top - g.header - LANDING_AIR_PX;
  const lowest = Math.max(0, g.floor ?? 0);
  return Math.round(Math.max(0, Math.min(g.maxScroll, Math.max(lowest, y))));
}

/** An element's top in px from the top of the page, read from the layout (a transform never moves it). */
function pageTopOf(el: HTMLElement): number {
  let y = 0;
  for (let n: HTMLElement | null = el; n; n = n.offsetParent as HTMLElement | null) y += n.offsetTop;
  return y;
}

/** The place a link to `el` means, in px from the top of the page: a section's words (its children), else itself. */
export function placeOf(el: Element): { top: number; bottom: number } {
  const parts = (el.tagName === 'SECTION' ? Array.from(el.children) : [el]).filter(
    (p): p is HTMLElement => p instanceof HTMLElement && p.offsetHeight > 0,
  );
  if (!parts.length) {
    if (!(el instanceof HTMLElement)) {
      const r = el.getBoundingClientRect();
      return { top: r.top + window.scrollY, bottom: r.bottom + window.scrollY };
    }
    const top = pageTopOf(el);
    return { top, bottom: top + el.offsetHeight };
  }
  const tops = parts.map(pageTopOf);
  return { top: Math.min(...tops), bottom: Math.max(...parts.map((p, i) => tops[i] + p.offsetHeight)) };
}

/** How far down the window the page's sticky or fixed header reaches (0 when it has none). */
export function headerCover(): number {
  let cover = 0;
  for (const h of Array.from(document.querySelectorAll('header'))) {
    const position = window.getComputedStyle(h).position;
    if (position !== 'sticky' && position !== 'fixed') continue;
    const r = h.getBoundingClientRect();
    if (r.height > 0 && r.top <= 1) cover = Math.max(cover, r.bottom);
  }
  return cover;
}

/** The highest scroll position a landing may rest at on this page, when the page names one. */
function floorOf(doc: Document): number | undefined {
  const mark = doc.querySelector(`[${LANDING_FLOOR_ATTRIBUTE}]`);
  if (!(mark instanceof HTMLElement)) return undefined;
  const margin = parseFloat(window.getComputedStyle(mark).scrollMarginTop) || 0;
  return pageTopOf(mark) - margin;
}

/** Opens the <details> `el` is (or stands inside), and every closed <details> around that; returns how many it opened. */
export function openFoldsAround(el: Element): number {
  let opened = 0;
  for (let d = el.closest('details'); d; d = d.parentElement ? d.parentElement.closest('details') : null) {
    if (!d.open) {
      d.open = true;
      opened += 1;
    }
  }
  return opened;
}

/** What a person does that takes the page out of a landing's hands. */
const TAKES_THE_PAGE = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;

/** The last landing's watch on the band, ended by the next landing. */
let endWatch: (() => void) | null = null;

/**
 * Scrolls the page so the place `el` names lands as landingScrollTop says, gliding unless motion is reduced; and for
 * RELAND_WINDOW_MS lands it again when the chat bar's reach changes, until the person takes the page.
 */
export function landOn(el: Element): number {
  endWatch?.();
  const top = scrollToPlace(el);
  let timer = 0;
  const end = (): void => {
    stopBand();
    window.clearTimeout(timer);
    for (const type of TAKES_THE_PAGE) window.removeEventListener(type, end, true);
    if (endWatch === end) endWatch = null;
  };
  const stopBand = subscribeChatBarReach(() => {
    if (el.isConnected) scrollToPlace(el);
  });
  for (const type of TAKES_THE_PAGE) window.addEventListener(type, end, { capture: true, passive: true });
  timer = window.setTimeout(end, RELAND_WINDOW_MS);
  endWatch = end;
  return top;
}

function scrollToPlace(el: Element): number {
  const place = placeOf(el);
  const viewport = window.innerHeight;
  const top = landingScrollTop({
    ...place,
    viewport,
    header: headerCover(),
    footer: getChatBarReach(),
    maxScroll: Math.max(0, document.documentElement.scrollHeight - viewport),
    floor: floorOf(el.ownerDocument),
  });
  const still = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({ top, behavior: still ? 'auto' : 'smooth' });
  return top;
}
